import type {
  WidgetApiConfig, ApiData, ClientSearchResult, ClientProfile,
  Booking, Sale, WalletData, ClientPackage, ClientMembership,
  FormSubmission, PackageTemplate, MembershipPlanPublic, RecurringSeries,
  OtpVerifyResult, Provider,
} from './types';
import { TIMING } from './constants';

declare const process: { env: { NODE_ENV?: string } } | undefined;

function normalizeBase(apiBase: string): string {
  return apiBase.replace(/\/+$/, '');
}

// ── Client token management ─────────────────────────────────────────────

const STORAGE_KEY = 'portal_session';

let _clientToken: string | null = null;

export function setClientToken(token: string | null, clientId?: number, expiresInDays?: number): void {
  _clientToken = token;
  if (token && clientId) {
    const expiresAt = Date.now() + (expiresInDays && expiresInDays > 0 ? expiresInDays * 86400000 : 86400000);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, clientId, expiresAt }));
    } catch { /* quota exceeded or private browsing */ }
  }
}

export function getClientToken(): string | null {
  return _clientToken;
}

export function getStoredSession(): { token: string; clientId: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data.token || !data.clientId || !data.expiresAt) return null;
    if (Date.now() > data.expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { token: data.token, clientId: data.clientId };
  } catch {
    return null;
  }
}

export function clearClientSession(): void {
  _clientToken = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* silent */ }
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (_clientToken) {
    headers['Authorization'] = `ClientBearer ${_clientToken}`;
  }
  return headers;
}

// ── OTP authentication ──────────────────────────────────────────────────

export async function requestOtp(apiBase: string, data: { phone?: string; email?: string }): Promise<{ status: string; message: string; delivered?: boolean; debug_code?: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/client-auth/request-otp/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!resp.ok) {
    // The server's own words matter here: a number that replied STOP is told
    // to text START (409 sms_stopped) instead of 'something went wrong'.
    let data: any = null;
    try { data = await resp.json(); } catch { data = null; }
    const err = new Error((data && data.error) || `OTP request failed: ${resp.statusText}`) as Error & { code?: string };
    err.code = data?.code;
    throw err;
  }
  return resp.json();
}

/**
 * "Nothing available — get back to me": name + phone (+ message) from the
 * date step land as an inbound message in the spa's INBOX (their own thread
 * when the number is on a client record).
 */
export async function sendBookingCallback(apiBase: string, data: {
  name: string; phone: string; email?: string; message?: string; preference?: 'either' | 'text' | 'call';
  date?: string; serviceName?: string;
}): Promise<{ ok: boolean; conversationId?: number }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/booking-callback/`, {
    method: 'POST',
    // Signed in: the server takes the name and number from the record.
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  if (!resp.ok) throw new Error(`Callback request failed: ${resp.status}`);
  return resp.json();
}

/** The client's own notes & preferences (shown on their record and every appointment). */
export async function savePortalNotes(apiBase: string, clientId: number, portalNotes: string): Promise<{ portalNotes: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/`, {
    method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ portalNotes }),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error((data && data.error) || 'Could not save your notes.');
  return { portalNotes: String(data?.portalNotes ?? portalNotes) };
}

/** Promotions by text / email on or off — the client's own choice (Tim,
 *  2026-10-31: stopping marketing texts should not need a STOP reply). */
export type ContactPrefField = 'marketingTexts' | 'marketingEmails' | 'apptTexts' | 'apptEmails';
export type ContactPrefs = Record<ContactPrefField, boolean>;

export async function saveMarketingPrefs(apiBase: string, clientId: number,
  prefs: Partial<ContactPrefs>): Promise<ContactPrefs> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/`, {
    method: 'PATCH', headers: authHeaders(), body: JSON.stringify(prefs),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error((data && data.error) || 'Could not save your choice.');
  return {
    marketingTexts: !!data?.marketingTexts, marketingEmails: !!data?.marketingEmails,
    apptTexts: data?.apptTexts !== false, apptEmails: data?.apptEmails !== false,
  };
}

/** The client's own details — birthday, gender, pronouns, socials, address
 *  (the portal PATCH allowlist; never staff notes or tags). */
export const PORTAL_DETAIL_FIELDS = ['birthday', 'gender', 'pronouns', 'instagram', 'facebook',
  'country', 'address1', 'address2', 'city', 'state', 'zip'] as const;
export type PortalDetailField = typeof PORTAL_DETAIL_FIELDS[number];
export type PortalDetails = Record<PortalDetailField, string>;

export async function savePortalDetails(apiBase: string, clientId: number, details: Partial<PortalDetails>): Promise<PortalDetails> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/`, {
    method: 'PATCH', headers: authHeaders(), body: JSON.stringify(details),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error((data && data.error) || 'Could not save your details.');
  const out = {} as PortalDetails;
  for (const k of PORTAL_DETAIL_FIELDS) out[k] = String(data?.[k] ?? '');
  return out;
}

/** The fill page for one of the client's own outstanding forms. */
export async function fetchFormFillLink(apiBase: string, submissionId: number): Promise<string> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/form-submissions/${submissionId}/link/`, { headers: authHeaders() });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error((data && data.error) || 'Could not open the form.');
  return String(data.fullUrl || data.url || '');
}

/** A fresh copy of a form the client filled in long ago ('update your intake'). */
export async function createPortalFormSubmission(apiBase: string, templateId: number, clientId: number, clientName: string): Promise<{ id: number }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/form-submissions/`, {
    method: 'POST', headers: authHeaders(),
    body: JSON.stringify({ templateId, clientId, clientName }),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error((data && data.error) || 'Could not start the form.');
  return data;
}

/** 'Email me this receipt' — to the address on the client's record. */
export async function emailMyReceipt(apiBase: string, saleId: number): Promise<{ emailSent: boolean; sentTo?: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/sales/${saleId}/send-receipt/`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ channels: ['email'] }),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error((data && data.error) || 'Could not send the receipt.');
  return data;
}

/** The standing portal link opened (/portal/?magic=…): trade the secret for a session. */
export async function magicLogin(apiBase: string, token: string): Promise<OtpVerifyResult> {
  const base = apiBase.replace(/\/$/, '');
  const resp = await fetch(`${base}/api/v1/client-auth/magic-link/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || 'This link is no longer valid.');
  return data as OtpVerifyResult;
}

export async function verifyOtp(apiBase: string, data: { phone?: string; email?: string; code: string; clientId?: number }): Promise<OtpVerifyResult> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/client-auth/verify-otp/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!resp.ok) throw new Error(`OTP verification failed: ${resp.statusText}`);
  return resp.json();
}

// ── Birthday authentication ──────────────────────────────────────────────

export async function verifyBirthday(apiBase: string, data: { phone: string; birthday: string; pickClientId?: number }): Promise<any> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/client-auth/verify-birthday/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return resp.json();
}

export async function saveBookingBirthday(apiBase: string, bookingId: number, data: { birthday: string; confirmationCode: string }): Promise<any> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/${bookingId}/client-birthday/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return resp.json();
}

// ── Client self-registration ─────────────────────────────────────────────

export interface RegisterClientData {
  registrationToken: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;          // required when the code was requested by email
  marketingEmails?: boolean;
  marketingTexts?: boolean;
}

export interface RegisterClientResult {
  status: string;
  token?: string;
  clientId?: number;
  expiresInDays?: number;
  error?: string;
}

export async function registerClient(apiBase: string, data: RegisterClientData): Promise<RegisterClientResult> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/client-auth/register/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const result = await resp.json();
  if (!resp.ok) {
    return { status: 'error', error: result.error || 'Registration failed. Please try again.' };
  }
  return result;
}

// ── Widget config ───────────────────────────────────────────────────────

export async function loadWidgetConfig(apiBase: string | undefined): Promise<WidgetApiConfig> {
  if (!apiBase || apiBase === 'mock') return {};
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/widget-config/`);
  if (!resp.ok) throw new Error(`Failed to load widget config: ${resp.statusText}`);
  return resp.json();
}

// ── Booking data (batch fetch) ──────────────────────────────────────────

export async function loadWidgetData(apiBase: string | undefined): Promise<ApiData> {
  if (!apiBase || apiBase === 'mock') {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') {
      throw new Error('Mock data is not available in production builds');
    }
    await new Promise(r => setTimeout(r, TIMING.MOCK_LOAD_DELAY));
    const { MOCK_DATA } = await import('./mock/data');
    return MOCK_DATA;
  }
  const base = normalizeBase(apiBase);
  const [categories, enhancementGroups, providers, formConfig, availabilityConfig, giftCardConfig, gcPromotions, bookingLocations] =
    await Promise.all([
      fetch(`${base}/api/v1/categories/`).then(r => r.json()).catch(() => []),
      fetch(`${base}/api/v1/enhancement-groups/`).then(r => r.json()).catch(() => []),
      fetch(`${base}/api/v1/providers/`).then(r => r.json()).catch(() => []),
      fetch(`${base}/api/v1/form-config/`).then(r => r.json()).catch(() => ({})),
      fetch(`${base}/api/v1/availability-config/`).then(r => r.json()).catch(() => ({})),
      fetch(`${base}/api/v1/gift-card-config/`).then(r => r.json()).catch(() => ({})),
      fetch(`${base}/api/v1/gift-card-promotions/active/`).then(r => r.json()).catch(() => []),
      fetch(`${base}/api/v1/booking-locations/`).then(r => r.json()).catch(() => []),
    ]);
  return { categories, enhancementGroups, providers, formConfig, availabilityConfig, giftCardConfig, gcPromotions, bookingLocations };
}

// ── Availability (real slots from API) ───────────────────────────────────

export interface AvailabilitySlot {
  time: string;
  providerId: string;
  available: boolean;
  spotsRemaining?: number;
}

export interface SameDayPromo { offerId: number; name: string; label: string; price: number; discountedPrice: number }
export interface AvailabilityResult { slots: AvailabilitySlot[]; sameDay?: boolean; promo?: SameDayPromo | null; quietHours?: { label: string; price: number } | null }

// One cart item as the availability endpoint's `chain` param needs it. The
// grid used to be computed for the FIRST service only, so a multi-service cart
// could be offered a day the second service has no provider on at all — the
// client found out after filling in the whole form.
export interface ChainItem {
  serviceId: number | string;
  staffId?: number | null;
  gender?: 'female' | 'male' | null;
  addonIds?: (number | string)[];
  guestIndex?: number;
}

export async function fetchAvailability(
  apiBase: string,
  date: string,
  serviceId: number | string,
  providerId?: number | string | null,
  addonIds?: (number | string)[],
  locationId?: number | null,
  sameDay?: boolean,
  chain?: ChainItem[] | null,
  gender?: 'female' | 'male' | null,
  excludeBookingId?: number | null,
): Promise<AvailabilityResult> {
  const base = normalizeBase(apiBase);
  const params = new URLSearchParams({ date, service_id: String(serviceId) });
  if (providerId) params.set('provider_id', String(providerId));
  // A reschedule: the booking's own rows do not hold their time (the server
  // honours it only for the booking's own client — the ClientBearer goes along).
  if (excludeBookingId) params.set('exclude_booking_id', String(excludeBookingId));
  if (addonIds && addonIds.length) params.set('addon_ids', addonIds.join(','));
  if (locationId) params.set('location_id', String(locationId));
  // "Open Today" engine for today's column (Settings → Same-day booking → Open Today rules)
  if (sameDay) params.set('same_day', '1');
  if (gender) params.set('gender', gender);
  if (chain && chain.length >= 2) {
    params.set('chain', JSON.stringify(chain.map(c => ({
      serviceId: c.serviceId,
      ...(typeof c.staffId === 'number' ? { staffId: c.staffId } : {}),
      ...(c.gender ? { gender: c.gender } : {}),
      ...(c.addonIds && c.addonIds.length ? { addonIds: c.addonIds } : {}),
      ...(c.guestIndex ? { guestIndex: c.guestIndex } : {}),
    }))));
  }
  try {
    const headers: Record<string, string> = {};
    const tok = excludeBookingId ? getClientToken() : null;
    if (tok) headers['Authorization'] = `ClientBearer ${tok}`;
    const resp = await fetch(`${base}/api/v1/availability/?${params}`, { headers });
    const data = await resp.json();
    return { slots: data.slots || [], sameDay: !!data.sameDay, promo: data.promo || null, quietHours: data.quietHours || null };
  } catch {
    return { slots: [] };
  }
}

// ── Promo code validation ───────────────────────────────────────────────

export interface PromoResult {
  valid: boolean;
  offerId?: number;
  offerName?: string;
  discountAmount?: number;
  discountLabel?: string;
  error?: string;
  /** a referral code the spa's rules refuse for this client (Tim, 2026-11-03) */
  code?: string;
  isReferral?: boolean;
}

export async function validatePromoCode(apiBase: string | undefined, code: string, totalAmount: number,
                                        items?: { serviceId: number | string; price: number; addons?: { id: number | string; name?: string; price: number }[] }[],
                                        phone?: string, forOther?: boolean): Promise<PromoResult> {
  if (!apiBase || apiBase === 'mock') {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') {
      return { valid: false, error: 'Mock mode is not available.' };
    }
    await new Promise(r => setTimeout(r, TIMING.PROMO_DELAY));
    if (code.toUpperCase() === 'SAVE20') {
      return { valid: true, offerId: 99, offerName: 'Demo Discount', discountAmount: 20, discountLabel: '$20 off' };
    }
    return { valid: false, error: 'Promo code not found or has expired.' };
  }
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/offers/validate-code/`, {
    method: 'POST',
    // A signed-in client (ClientBearer) or the phone typed says who is
    // redeeming — a referral code may be for new clients only / once.
    headers: authHeaders(),
    // items let the server scope the discount to the services the offer covers
    body: JSON.stringify({ code, totalAmount, items: items || undefined, phone: phone || undefined, forOther: forOther || undefined }),
  });
  if (!resp.ok) return { valid: false, error: 'Could not validate promo code.' };
  return resp.json();
}

// ── Package templates ───────────────────────────────────────────────────

export async function loadPackageTemplates(apiBase: string | undefined): Promise<PackageTemplate[]> {
  if (!apiBase || apiBase === 'mock') {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') return [];
    return [
      { id: 1, name: '6 Massages for the Price of 4', shortDescription: 'Enjoy 6 massages at a special package rate.', description: '', groupCount: 1, totalPrice: 400.0, expiresEnabled: true, expiresAmount: 12, expiresUnit: 'months', availableOnline: true, onlyDirectLink: false, requireAgreement: false, agreementText: '' },
      { id: 2, name: 'Spa Day Bundle', shortDescription: 'A full day of relaxation and rejuvenation.', description: '', groupCount: 2, totalPrice: 220.0, expiresEnabled: true, expiresAmount: 6, expiresUnit: 'months', availableOnline: true, onlyDirectLink: false, requireAgreement: false, agreementText: '' },
    ];
  }
  const resp = await fetch(`${normalizeBase(apiBase)}/api/v1/package-templates/public/`);
  if (!resp.ok) throw new Error(`Failed to load packages: ${resp.statusText}`);
  return resp.json();
}

// ── Membership plans (public) ────────────────────────────────────────────

export async function loadMembershipPlans(apiBase: string | undefined): Promise<MembershipPlanPublic[]> {
  if (!apiBase || apiBase === 'mock') {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') return [];
    return [
      { id: 1, name: 'Monthly Wellness', type: 'services', price: 99, billingInterval: 1, billingUnit: 'month', description: 'One massage per month with 10% off products.', discountProducts: true, discountProductsPercent: 10, discountServices: false, discountServicesPercent: 0, requireAgreement: false, agreementText: '' },
    ];
  }
  const resp = await fetch(`${normalizeBase(apiBase)}/api/v1/membership-plans/public/`);
  if (!resp.ok) throw new Error(`Failed to load membership plans: ${resp.statusText}`);
  return resp.json();
}

export async function enrollMembership(apiBase: string, data: Record<string, unknown>): Promise<any> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/client-memberships/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => null);
    throw new Error((errData && errData.error) || 'Enrollment failed. Please try again.');
  }
  return resp.json();
}

// ── Portal purchase / enrollment ─────────────────────────────────────

export async function portalPurchasePackage(apiBase: string, templateId: number): Promise<any> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/client-packages/`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ templateId }),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => null);
    throw new Error((errData && errData.error) || 'Purchase failed. Please try again.');
  }
  return resp.json();
}

export async function portalEnrollMembership(
  apiBase: string, planId: number, termsAccepted = false,
): Promise<any> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/client-memberships/`, {
    method: 'POST',
    headers: authHeaders(),
    // The server refuses without this on a plan that requires terms, so the
    // tick is the agreement rather than a UI formality.
    body: JSON.stringify({ planId, termsAccepted }),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => null);
    throw new Error((errData && errData.error) || 'Enrollment failed. Please try again.');
  }
  return resp.json();
}

export async function fetchCreditCards(apiBase: string, clientId: number): Promise<any[]> {
  const base = normalizeBase(apiBase);
  const res = await fetch(`${base}/api/v1/clients/${clientId}/credit-cards/`, { headers: authHeaders() });
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function addCreditCard(apiBase: string, clientId: number, card: { last4: string; brand: string; expMonth: number; expYear: number } | { stripePaymentMethodId: string }): Promise<any> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/credit-cards/`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(card),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => null);
    throw new Error((errData && errData.error) || 'Could not save card.');
  }
  return resp.json();
}

export async function setDefaultCreditCard(apiBase: string, clientId: number, cardId: number): Promise<any> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/credit-cards/${cardId}/`, {
    method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ isDefault: true }),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => null);
    throw new Error((errData && errData.error) || 'Could not update the card.');
  }
  return resp.json();
}

export async function deleteCreditCard(apiBase: string, clientId: number, cardId: number): Promise<void> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/credit-cards/${cardId}/`, {
    method: 'DELETE', headers: authHeaders(),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => null);
    throw new Error((errData && errData.error) || 'Could not remove the card.');
  }
}

export interface BalancePaymentResult {
  ok: boolean; saleId: number; saleNumber: string; amount: number;
  accountBalance: number; cardBrand: string; cardLast4: string;
}

/** Pay into the account balance from the portal (fund it, or settle what is
 *  owed). One of `cardId` (a card on file) or `paymentMethodToken` (a new
 *  card); `attemptId` makes a double tap settle one charge. */
export async function payAccountBalance(apiBase: string, clientId: number, body: {
  amount: number; cardId?: number; paymentMethodToken?: string; saveCard?: boolean; attemptId: string;
}): Promise<BalancePaymentResult> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/account-balance/pay/`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) {
    const err = new Error((data && data.error) || 'The payment could not be completed.') as Error & { status?: number; code?: string };
    err.status = resp.status; err.code = data?.code;
    throw err;
  }
  return data as BalancePaymentResult;
}

// ── Providers reload (with location filter) ─────────────────────────────

export async function loadProviders(apiBase: string, locationId?: number): Promise<Provider[]> {
  const base = normalizeBase(apiBase);
  const params = locationId ? `?location_id=${locationId}` : '';
  const resp = await fetch(`${base}/api/v1/providers/${params}`);
  if (!resp.ok) throw new Error(`Failed to load providers: ${resp.statusText}`);
  return resp.json();
}

// ── Client data (authenticated) ─────────────────────────────────────────

export async function searchClients(apiBase: string, query: string): Promise<ClientSearchResult[]> {
  const base = normalizeBase(apiBase);
  const res = await fetch(`${base}/api/v1/clients/?q=${encodeURIComponent(query)}`, { headers: authHeaders() });
  const data = await res.json();
  return Array.isArray(data) ? data : data.results || [];
}

export async function fetchClientProfile(apiBase: string, clientId: number): Promise<ClientProfile> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/`, { headers: authHeaders() });
  if (!resp.ok) throw new Error(`Failed to load client profile: ${resp.statusText}`);
  return resp.json();
}

export async function fetchBookings(apiBase: string, clientId: number): Promise<Booking[]> {
  const base = normalizeBase(apiBase);
  const res = await fetch(`${base}/api/v1/bookings/?client_id=${clientId}`, { headers: authHeaders() });
  const data = await res.json();
  return Array.isArray(data) ? data : data.results || [];
}

export async function fetchSales(apiBase: string, clientId: number): Promise<Sale[]> {
  const base = normalizeBase(apiBase);
  const res = await fetch(`${base}/api/v1/sales/?client_id=${clientId}`, { headers: authHeaders() });
  const data = await res.json();
  return Array.isArray(data) ? data : data.results || [];
}

export async function fetchWallet(apiBase: string, clientId: number): Promise<WalletData> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/wallet/`, { headers: authHeaders() });
  if (!resp.ok) throw new Error(`Failed to load wallet: ${resp.statusText}`);
  return resp.json();
}

export async function fetchClientPackages(apiBase: string, clientId: number): Promise<ClientPackage[]> {
  const base = normalizeBase(apiBase);
  const res = await fetch(`${base}/api/v1/client-packages/?client_id=${clientId}`, { headers: authHeaders() });
  const data = await res.json();
  return Array.isArray(data) ? data : data.results || [];
}

export async function fetchSaleDetail(apiBase: string, saleId: number): Promise<Sale> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/sales/${saleId}/`, { headers: authHeaders() });
  if (!resp.ok) throw new Error(`Failed to load sale detail: ${resp.statusText}`);
  return resp.json();
}

export async function fetchClientMemberships(
  apiBase: string, clientId: number, token?: string,
): Promise<ClientMembership[]> {
  const base = normalizeBase(apiBase);
  // An explicit token is the recognition one: narrow scope, and NOT the portal
  // session, so it must not go through the shared session header.
  const headers = token
    ? { Authorization: `ClientBearer ${token}` }
    : authHeaders();
  const res = await fetch(`${base}/api/v1/client-memberships/?client_id=${clientId}`, { headers });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : data.results || [];
}

// ── Form submissions ────────────────────────────────────────────────────

export async function fetchFormSubmissions(apiBase: string, clientId: number): Promise<FormSubmission[]> {
  const base = normalizeBase(apiBase);
  const res = await fetch(`${base}/api/v1/form-submissions/?client_id=${clientId}`, { headers: authHeaders() });
  const data = await res.json();
  return Array.isArray(data) ? data : (data.results || []);
}

export async function fetchFormSubmissionDetail(apiBase: string, submissionId: number): Promise<FormSubmission> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/form-submissions/${submissionId}/`, { headers: authHeaders() });
  if (!resp.ok) throw new Error(`Failed to load form submission: ${resp.statusText}`);
  return resp.json();
}

// ── Client management ───────────────────────────────────────────────────

export async function createClient(apiBase: string, data: { firstName: string; lastName: string; email?: string; phone?: string }): Promise<{ id: number; name: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  if (!resp.ok) throw new Error('Could not create client');
  return resp.json();
}

export async function createClientAlias(apiBase: string, clientId: number, data: { firstName: string; lastName: string; email?: string }): Promise<{ id: number; firstName: string; lastName: string; email: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/clients/${clientId}/aliases/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!resp.ok) throw new Error('Could not create alias');
  return resp.json();
}

// ── Booking submission ──────────────────────────────────────────────────

export async function submitBooking(apiBase: string, payload: unknown): Promise<any> {
  if (!apiBase || apiBase === 'mock') {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') {
      throw new Error('Mock mode is not available in production.');
    }
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      console.log('[BookingWidget] Booking payload:', JSON.stringify(payload, null, 2));
    }
    await new Promise(r => setTimeout(r, TIMING.BOOKING_DELAY));
    return {};
  }
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => null);
    const err = new Error((data && data.error) || 'Booking could not be submitted. Please try again.') as BookingRefusedError;
    // A refusal the widget can ANSWER (409 hold_exists: the person already
    // holds a time for its deposit) rides on the error, not just its text.
    err.code = (data && data.code) || undefined;
    err.data = data || undefined;
    throw err;
  }
  return resp.json().catch(() => ({}));
}

export interface BookingRefusedError extends Error {
  code?: string;
  data?: any;
}

/** Outcome of a queued widget booking (GET /quick-book/pending/<token>/).
 *
 * Production writes bookings through the queue, so the booking — and its
 * deposit payment link — do not exist yet when POST /bookings/ answers 202.
 */
export async function fetchPendingBooking(apiBase: string, token: string): Promise<any> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/quick-book/pending/${encodeURIComponent(token)}/`, { cache: 'no-store' });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}

// ── In-widget deposit step ───────────────────────────────────────────

/** Anonymous "does this number book here?" — also used by the regular flow
 *  to collapse the contact form for a returning client. */
export async function lookupPhone(
  apiBase: string, phone: string,
  ctx: { serviceId?: number | string | null; staffId?: number | string | null } = {},
): Promise<{ known: boolean; cardOnFile?: boolean; membershipCoversProvider?: boolean | null }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/quick-book/lookup/`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      phone, surface: 'booking',
      // Named so the server can answer about THIS provider and nothing else.
      serviceId: ctx.serviceId ?? null, staffId: ctx.staffId ?? null,
    }),
  });
  if (!resp.ok) return { known: false };
  return resp.json();
}

export async function payDepositLink(apiBase: string, token: string, paymentMethodToken: string,
                                     giftCardNumber?: string, policyAgreed?: boolean): Promise<any> {
  const base = normalizeBase(apiBase);
  const body: Record<string, string | boolean> = { paymentMethodToken };
  if (giftCardNumber && giftCardNumber.trim()) body.giftCardNumber = giftCardNumber.trim();
  if (policyAgreed) body.policyAgreed = true;
  const resp = await fetch(`${base}/api/v1/payment-links/${encodeURIComponent(token)}/pay/`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err: any = new Error(data.error || 'Payment failed. Please try again.');
    err.status = resp.status; err.linkStatus = data.status;
    throw err;
  }
  return data;
}

/** The server's word on a saved deposit hold: still pending (resume it), paid, expired or cancelled. */
export async function resolveDepositLink(apiBase: string, token: string): Promise<{ status: string; amount?: number; payExpiresAt?: string } | null> {
  const base = normalizeBase(apiBase);
  try {
    const resp = await fetch(`${base}/api/v1/payment-links/${encodeURIComponent(token)}/`);
    const data = await resp.json().catch(() => ({}));
    if (resp.status === 404) return { status: 'gone' };
    return { status: data.status || (resp.ok ? 'pending' : 'gone'), amount: data.amount, payExpiresAt: data.payExpiresAt };
  } catch { return null; }
}

/** "Cancel and release my reservation" on the deposit step. */
export async function cancelDepositLink(apiBase: string, token: string): Promise<{ ok: boolean; error?: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/payment-links/${encodeURIComponent(token)}/cancel/`, { method: 'POST' });
  const data = await resp.json().catch(() => ({}));
  return { ok: resp.ok || resp.status === 410, error: data.error };
}

export async function textDepositLink(apiBase: string, token: string): Promise<{ smsSent: boolean; phoneLast4?: string; alreadySent?: boolean }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/payment-links/${encodeURIComponent(token)}/send-sms/`, { method: 'POST' });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || 'Could not send the text.');
  return data;
}

// ── Client self-service cancel / reschedule ─────────────────────────

/** Confirm / 'I'm here' from the portal's appointment details (Tim, 2026-10-18). */
export async function clientConfirmBooking(apiBase: string, bookingId: number): Promise<{ ok: boolean; status?: string; error?: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/${bookingId}/client-confirm/`, { method: 'POST', headers: authHeaders() });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) return { ok: false, error: data?.error || `Could not confirm: ${resp.statusText}` };
  return data;
}

/** Apply a portal offer on the client's record to an appointment already on
 *  the books (Tim, 2026-10-18): the server reserves it and re-prices a pending deposit. */
export async function clientApplyOffer(apiBase: string, bookingId: number, promotionId: number): Promise<{ ok: boolean; error?: string; code?: string; promoCode?: string; discountAmount?: number }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/${bookingId}/apply-offer/`, {
    method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ promotionId }),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) return { ok: false, error: data?.error || `Could not apply the offer: ${resp.statusText}`, code: data?.code };
  return data;
}

export async function clientCheckinBooking(apiBase: string, bookingId: number): Promise<{ ok: boolean; status?: string; error?: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/${bookingId}/client-checkin/`, { method: 'POST', headers: authHeaders() });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) return { ok: false, error: data?.error || `Could not check in: ${resp.statusText}` };
  return data;
}

export async function clientCancelBooking(apiBase: string, bookingId: number): Promise<{ ok: boolean; error?: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/${bookingId}/client-cancel/`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => null);
    return { ok: false, error: errData?.error || `Cancel failed: ${resp.statusText}` };
  }
  return resp.json();
}

export async function clientRescheduleBooking(apiBase: string, bookingId: number, date: string, time: string,
  allowProviderChange = false): Promise<{ ok?: boolean; error?: string; date?: string; time?: string;
    providerChanges?: { serviceName: string; from: string; to: string }[] }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/${bookingId}/client-reschedule/`, {
    method: 'POST',
    headers: authHeaders(),
    // allowProviderChange: they accepted 'this time may be with someone else'
    // for a therapist they had asked for (a non-requested one moves freely).
    body: JSON.stringify({ date, time, allowProviderChange }),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => null);
    return { ok: false, error: errData?.error || `Reschedule failed: ${resp.statusText}` };
  }
  return resp.json();
}


/** Ask the business to reschedule / cancel (self-service off, or too close to the appointment). */
export async function clientRequestChange(
  apiBase: string, bookingId: number,
  body: { kind: 'cancel' | 'reschedule'; date?: string; time?: string; message?: string },
): Promise<{ ok: boolean; error?: string; code?: string }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/${bookingId}/client-request/`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok) return { ok: false, error: data?.error || 'Could not send your request.', code: data?.code };
  return { ok: true };
}

export async function clientWithdrawRequest(apiBase: string, bookingId: number): Promise<{ ok: boolean }> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/bookings/${bookingId}/client-request/withdraw/`, {
    method: 'POST', headers: authHeaders(),
  });
  return { ok: resp.ok };
}

// ── Analytics ───────────────────────────────────────────────────────────────

let _analyticsApiBase = '';
let _analyticsSessionId = '';
const _eventQueue: Array<{ type: string; blockType: string; blockIndex: number | null; metadata: Record<string, unknown>; ts: number }> = [];
let _flushInterval: ReturnType<typeof setInterval> | null = null;

const VISIT_KEY = 'cld_visit';
const VISIT_IDLE_MS = 30 * 60 * 1000;

/** One id per VISIT to the site (this tab, until 30 min idle), so a visit's
 *  pages, a booking-widget open and the booking it became join up. Kept in
 *  sessionStorage — no cookie, gone when the tab closes. */
export function visitSessionId(): string {
  const now = Date.now();
  try {
    const raw = sessionStorage.getItem(VISIT_KEY);
    const v = raw ? JSON.parse(raw) : null;
    const id = v && typeof v.id === 'string' && now - Number(v.at || 0) < VISIT_IDLE_MS ? v.id : crypto.randomUUID();
    sessionStorage.setItem(VISIT_KEY, JSON.stringify({ id, at: now }));
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export function initAnalytics(apiBase: string, sessionId?: string): void {
  _analyticsApiBase = normalizeBase(apiBase);
  _analyticsSessionId = sessionId || visitSessionId();
  if (_flushInterval) clearInterval(_flushInterval);
  _flushInterval = setInterval(flushWidgetAnalytics, 5000);
}

export function getAnalyticsSessionId(): string {
  return _analyticsSessionId;
}

export function trackWidgetEvent(type: string, metadata: Record<string, unknown> = {}): void {
  if (!_analyticsApiBase) return;
  _eventQueue.push({ type, blockType: '', blockIndex: null, metadata, ts: Date.now() });
}

/** Where a visit came from, from the page URL's utm_* tags. */
export function utmFrom(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const q = new URLSearchParams(search);
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign']) {
      const v = (q.get(k) || '').trim();
      if (v) out[k.replace('utm_', '')] = v.slice(0, 100);
    }
  } catch { /* ignore */ }
  return out;
}

/** WEBSITE ANALYTICS (Tim, 2026-09-25): one `site_page_view` per page of the
 *  spa's own site the script runs on — path, the page's referrer and utm tags.
 *  No cookie, no personal data. Sent at once (keepalive) so a quick bounce
 *  still counts. */
export function trackSitePageView(): void {
  if (!_analyticsApiBase || typeof window === 'undefined') return;
  const loc = window.location;
  trackWidgetEvent('site_page_view', {
    path: (loc.pathname || '/').slice(0, 300),
    title: (document.title || '').slice(0, 150),
    host: (loc.host || '').replace(/^www\./, ''),
    ref: (document.referrer || '').slice(0, 500),
    ...utmFrom(loc.search),
  });
  flushWidgetAnalytics();
}

export function flushWidgetAnalytics(): void {
  if (!_eventQueue.length || !_analyticsApiBase) return;
  const events = _eventQueue.splice(0);
  const payload = JSON.stringify({
    sessionId: _analyticsSessionId,
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    events,
  });
  try {
    fetch(`${_analyticsApiBase}/api/v1/lp-events/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* silent */ }
}

export function stopWidgetAnalytics(): void {
  if (_flushInterval) { clearInterval(_flushInterval); _flushInterval = null; }
  flushWidgetAnalytics();
}


// ── Recurring Series ────────────────────────────────────────────────────

export async function fetchRecurringSeries(apiBase: string): Promise<RecurringSeries[]> {
  const base = normalizeBase(apiBase);
  const res = await fetch(`${base}/api/v1/recurring-series/?status=active`, { headers: authHeaders() });
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchRecurringDetail(apiBase: string, seriesId: number): Promise<RecurringSeries> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/recurring-series/${seriesId}/`, { headers: authHeaders() });
  if (!resp.ok) throw new Error(`Failed to load recurring series: ${resp.statusText}`);
  return resp.json();
}

export async function skipRecurringOccurrence(apiBase: string, seriesId: number, date: string): Promise<void> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/recurring-series/${seriesId}/skip/`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ date }),
  });
  if (!resp.ok) throw new Error(`Failed to skip occurrence: ${resp.statusText}`);
}

export async function cancelRecurringSeries(apiBase: string, seriesId: number): Promise<void> {
  const base = normalizeBase(apiBase);
  const resp = await fetch(`${base}/api/v1/recurring-series/${seriesId}/`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!resp.ok) throw new Error(`Failed to cancel series: ${resp.statusText}`);
}


// ── Referral Program ───────────────────────────────────────────────────

export async function fetchReferralCode(apiBase: string): Promise<{
  code: string; enabled: boolean; discountLabel: string; rewardDescription: string;
  shareUrl?: string; referredCount?: number; pendingCount?: number; earned?: number;
  terms?: string[];
} | null> {
  const base = normalizeBase(apiBase);
  try {
    const res = await fetch(`${base}/api/v1/referrals/my-code/`, { headers: authHeaders() });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}


// ── Punch Cards ─────────────────────────────────────────────────────────

export interface PunchCard {
  id: number;
  templateId: number;
  templateName: string;
  targetType: string;
  targetName: string;
  currentPunches: number;
  punchesNeeded: number;
  status: string;
  rewardType: string;
  rewardValue: number;
  rewardServiceName: string;
}

export async function fetchPunchCards(apiBase: string): Promise<PunchCard[]> {
  const base = normalizeBase(apiBase);
  try {
    const res = await fetch(`${base}/api/v1/punch-cards/my-cards/`, { headers: authHeaders() });
    if (!res.ok) return [];
    return res.json();
  } catch {
    return [];
  }
}

// ── Loyalty Program ─────────────────────────────────────────────────────

export async function fetchLoyaltyPoints(apiBase: string): Promise<{
  enabled: boolean; points: number; lifetimePoints: number;
  availableTiers: Array<{ name: string; pointsCost: number; description: string }>;
  nextTier: { name: string; pointsCost: number } | null;
  pointsToNextTier: number;
  transactions: any[];
} | null> {
  const base = normalizeBase(apiBase);
  try {
    const res = await fetch(`${base}/api/v1/loyalty/my-points/`, { headers: authHeaders() });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

// ── Add to my calendar ──────────────────────────────────────────────────────

/** `GET /bookings/<id>/event.ics` (ClientBearer) → the file's text, or null. */
export async function fetchBookingIcs(apiBase: string, bookingId: number): Promise<string | null> {
  const tok = getClientToken();
  if (!tok) return null;
  try {
    const resp = await fetch(`${normalizeBase(apiBase)}/api/v1/bookings/${bookingId}/event.ics`, {
      headers: { 'Authorization': `ClientBearer ${tok}` },
    });
    if (!resp.ok) return null;
    return await resp.text();
  } catch {
    return null;
  }
}

// ── Client push notifications + the client's own portal link (Tim, 2026-11-02) ──

export interface ClientPushStatus { enabled: boolean; devices: number; vapidKey: string }

export async function fetchClientPushStatus(apiBase: string): Promise<ClientPushStatus | null> {
  try {
    const res = await fetch(`${normalizeBase(apiBase)}/api/v1/client-auth/push/`, { headers: authHeaders() });
    return res.ok ? res.json() : null;
  } catch { return null; }
}

export async function subscribeClientPush(apiBase: string, sub: PushSubscriptionJSON, previousEndpoint?: string | null): Promise<{ ok: boolean; devices: number }> {
  const res = await fetch(`${normalizeBase(apiBase)}/api/v1/client-auth/push/subscribe/`, {
    method: 'POST', headers: authHeaders(),
    body: JSON.stringify({ endpoint: sub.endpoint, keys: sub.keys, previousEndpoint: previousEndpoint || '' }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || 'Could not switch notifications on.');
  return { ok: true, devices: Number(data?.devices || 0) };
}

export async function unsubscribeClientPush(apiBase: string, endpoint: string): Promise<{ devices: number }> {
  const res = await fetch(`${normalizeBase(apiBase)}/api/v1/client-auth/push/unsubscribe/`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ endpoint }),
  });
  const data = await res.json().catch(() => null);
  return { devices: Number(data?.devices || 0) };
}

export async function testClientPush(apiBase: string): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`${normalizeBase(apiBase)}/api/v1/client-auth/push/test/`, { method: 'POST', headers: authHeaders() });
  const data = await res.json().catch(() => null);
  return { ok: !!(res.ok && data?.ok), error: data?.error };
}

/** The client's standing portal link token — the installed app's start URL carries it. */
export async function fetchOwnPortalLink(apiBase: string): Promise<{ token: string; url: string } | null> {
  try {
    const res = await fetch(`${normalizeBase(apiBase)}/api/v1/client-auth/portal-link/`, { headers: authHeaders() });
    return res.ok ? res.json() : null;
  } catch { return null; }
}

/** base64url → the Uint8Array pushManager.subscribe wants for applicationServerKey. */
export function vapidKeyBytes(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** The portal's review reminder: 'later' (snooze), 'done' (left one), 'reset' (show again). */
export async function postReviewPrompt(apiBase: string, action: 'later' | 'done' | 'reset'): Promise<{ reviewPromptDoneAt: string | null; reviewPromptSnoozedUntil: string | null }> {
  const res = await fetch(`${normalizeBase(apiBase)}/api/v1/client-auth/review-prompt/`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ action }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || 'Could not save that.');
  return { reviewPromptDoneAt: data?.reviewPromptDoneAt ?? null, reviewPromptSnoozedUntil: data?.reviewPromptSnoozedUntil ?? null };
}

// ── Tags the client manages themselves (Tim, 2026-11-02) ──────────────────────
export interface PortalTagGroup { label: string; exclusive: boolean; tags: { id: number; label: string; description: string; on: boolean }[] }

export async function fetchPortalTags(apiBase: string): Promise<PortalTagGroup[]> {
  try {
    const res = await fetch(`${normalizeBase(apiBase)}/api/v1/client-auth/tags/`, { headers: authHeaders() });
    if (!res.ok) return [];
    return (await res.json()).groups || [];
  } catch { return []; }
}

export async function setPortalTag(apiBase: string, tagId: number, on: boolean): Promise<PortalTagGroup[]> {
  const res = await fetch(`${normalizeBase(apiBase)}/api/v1/client-auth/tags/`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ tagId, on }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || 'Could not save that.');
  return data.groups || [];
}

/** The referral share link as a QR code (PNG) — fetched with the client's token, handed back as an object URL. */
export async function fetchReferralQr(apiBase: string): Promise<string | null> {
  const tok = getClientToken();
  if (!tok) return null;
  try {
    const res = await fetch(`${normalizeBase(apiBase)}/api/v1/referrals/my-code/qr.png`, { headers: { 'Authorization': `ClientBearer ${tok}` } });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch { return null; }
}
